import { useCallback, useEffect, useMemo, useState } from "react";

import { ConditionEditor, referencedFields, type Node as ConditionNode } from "./Conditions";
import { useScrollLock } from "../useScrollLock";
import { PageHead } from "./Shell";
import {
  AdminError,
  admin,
  saveFile,
  type BundledDoc,
  type DatapointRow,
  type KycTypes,
  type TextOverride,
} from "./api";

const TYPES = [
  "text", "number", "date", "select", "checkbox", "file", "idenfy",
  "address", "simple_sign", "note", "system", "set_flag",
  "idenfy-wiza", "idenfy-karta", "idenfy-paszport", "idenfy-dowod", "idenfy-driverlicence",
];
const LANGS = ["pl", "en", "ru", "uk"] as const;
const UI_FIELDS = ["title", "subtitle", "hint", "placeholder"] as const;

const EMPTY: DatapointRow = {
  key: "", index: 0, datapoint_type: "text", stage: "dane",
  ui_params: {}, llm_params: {}, options: null, validation: null,
  conditions: null, outcome_def: null, default_value: null,
  severity: null, blocking: false,
  extraction_targets: null, optional: false, risk: null, risk_values: null,
  file_params: null, flag: null, value_mapping: null,
};

/**
 * Węzły procesu. Każdy zapis przechodzi walidację schematu po stronie serwera —
 * zmiana, która rozbiłaby graf (duplikat indeksu, warunek nie do spełnienia,
 * odwołanie do nieistniejącego pola), nie zostanie przyjęta.
 */
export function Schema({
  schemaId,
  schemas = [],
  onSchema,
}: {
  schemaId: string;
  schemas?: string[];
  onSchema?: (id: string) => void;
}) {
  const [items, setItems] = useState<DatapointRow[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [editing, setEditing] = useState<DatapointRow | null>(null);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await admin.datapoints(schemaId);
      setItems(data.items);
      setProblems(data.problems);
      // Etapy tego procesu plus nazwy znane z pierwszego — przy pustym
      // schemacie nie byłoby z czego wybierać, a wpisać można dowolną.
      setStages([...new Set([...data.stages, ...data.known_stages])]);
      setError(null);
    } catch (caught) {
      setError((caught as AdminError).message);
    }
  }, [schemaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createProcess = async () => {
    const id = window.prompt("Identyfikator procesu (małe litery, cyfry, podkreślenia):");
    if (!id?.trim()) return;
    const title = window.prompt("Nazwa procesu dla ludzi (opcjonalnie):") ?? "";

    try {
      await admin.createSchema(id.trim(), title.trim());
      onSchema?.(id.trim());
    } catch (caught) {
      setError((caught as AdminError).message);
    }
  };

  const deps = useMemo(() => dependencyMap(items), [items]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      if (stageFilter && item.stage !== stageFilter) return false;
      if (!needle) return true;
      const title = item.ui_params?.title?.pl ?? "";
      return item.key.toLowerCase().includes(needle) || title.toLowerCase().includes(needle);
    });
  }, [items, query, stageFilter]);

  const importFile = async (file: File) => {
    try {
      const parsed = JSON.parse(await file.text());
      const rows: DatapointRow[] = parsed.datapoints ?? parsed;
      // Cały plik: węzły + treści dokumentów + ustawienia. Bez templates/settings
      // proces wgrywał się bez umowy i bez mapowań integracji.
      const result = await admin.importSchema(schemaId, {
        datapoints: rows,
        templates: parsed.templates,
        settings: parsed.settings,
        title: parsed.title,
      });
      await load();
      window.alert(
        `Wgrano ${result.imported} węzłów` +
          (result.templates ? `, ${result.templates} dokument(ów)` : "") +
          ".",
      );
    } catch (caught) {
      const failure = caught as AdminError;
      setError([failure.message, ...(failure.problems ?? [])].join("\n"));
    }
  };

  return (
    <section className="adm__section">
      <PageHead
        title="Schemat procesu"
        subtitle={`${items.length} węzłów · każdy zapis przechodzi walidację całego grafu`}
        actions={
          <>
            {/* Bez tego panel pokazywał pierwszy schemat z bazy — czyli
                przy dwóch procesach losowy, bez możliwości przejścia do drugiego. */}
            {schemas.length > 1 && onSchema && (
              <select
                className="select"
                value={schemaId}
                onChange={(event) => onSchema(event.target.value)}
                aria-label="Proces"
              >
                {schemas.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            )}
            {/* Nowy proces powstawał wyłącznie przez wgranie gotowego pliku
                JSON, czyli poza panelem. */}
            {onSchema && (
              <button className="btn btn--secondary" onClick={createProcess}>
                + Nowy proces
              </button>
            )}
            <button
              className="btn btn--secondary"
              onClick={() =>
                saveFile(admin.exportUrl(schemaId), `${schemaId}.json`).catch((caught) =>
                  setError((caught as AdminError).message),
                )
              }
            >
              Eksport
            </button>
            <button className="btn btn--primary" onClick={() => setEditing({ ...EMPTY, index: nextIndex(items) })}>
              + Nowy węzeł
            </button>
          </>
        }
      />

      <div className="adm__toolbar">
        <input
          className="input"
          type="search"
          placeholder="Szukaj po kluczu albo treści pytania"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <select className="select" value={stageFilter} onChange={(event) => setStageFilter(event.target.value)}>
          <option value="">Wszystkie etapy</option>
          {stages.map((stage) => (
            <option key={stage} value={stage}>
              {stage}
            </option>
          ))}
        </select>
        <span className="adm__count">{visible.length} z {items.length}</span>
        <span className="adm__spacer" />
        <label className="btn btn--secondary">
          Import
          <input
            type="file"
            accept="application/json"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) void importFile(file);
            }}
          />
        </label>
      </div>

      {problems.length > 0 && (
        <div className="adm__alert" role="alert">
          <strong>Schemat ma {problems.length} problemów:</strong>
          <ul>
            {problems.slice(0, 8).map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}
      {error && <pre className="adm__alert adm__alert--error">{error}</pre>}

      <div className="adm__tablewrap">
        <table className="adm__table adm__table--dense">
          <thead>
            <tr>
              <th>Index</th>
              <th>Klucz</th>
              <th>Typ</th>
              <th>Etap</th>
              <th>Pytanie (PL)</th>
              <th>Warunek</th>
              <th title="W ilu innych warunkach występuje to pole">Używane w</th>
              <th />
            </tr>
          </thead>
          {/* Kaskada z tokens.css — węzły wchodzą kolejno po zmianie filtra;
              reduced-motion sprowadza wejście do samego krycia. */}
          <tbody className="kaskada">
            {visible.map((item, index) => (
              <tr
                key={item.key}
                style={{ "--i": index } as React.CSSProperties}
                onClick={() => setEditing({ ...item })}
              >
                <td className="adm__mono">{item.index}</td>
                <td className="adm__mono">{item.key}</td>
                <td>
                  <span className="pill pill--type">{item.datapoint_type}</span>
                </td>
                <td className="adm__muted">{item.stage ?? "—"}</td>
                <td>{item.ui_params?.title?.pl ?? "—"}</td>
                <td className="adm__muted">{item.conditions ? "tak" : "—"}</td>
                <td className="adm__muted">
                  {(deps.usedBy.get(item.key)?.length ?? 0) || "—"}
                </td>
                {/* Span, żeby hover dosuwał strzałkę transformem bez ruszania
                    komórki (linie tabeli zostają na miejscu). */}
                <td className="adm__chev">
                  <span aria-hidden="true">›</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Editor
          // Skok do sąsiada podmienia `row`, a stan formularza żyje w useState —
          // bez klucza w polach zostałyby dane poprzedniego węzła.
          key={editing.key}
          schemaId={schemaId}
          row={editing}
          stages={stages}
          fields={items}
          exists={items.some((item) => item.key === editing.key)}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
          onOpen={(key) => {
            const target = items.find((item) => item.key === key);
            if (target) setEditing({ ...target });
          }}
        />
      )}
    </section>
  );
}

/**
 * Punkt wyjścia z procesu — jeden wybór zamiast dwóch pól.
 *
 * W bazie zostają dwie kolumny (`severity` i `blocking`), bo tak czyta je
 * silnik i statystyki: „final" znaczy koniec ścieżki, „blocking" — że sprawa
 * czeka na człowieka, a nie zamyka się sama. Konsultantowi nie ma to jak
 * pomóc: interesuje go, czy ten ekran kończy sprawę dobrze, czy źle.
 */
function exitKind(row: DatapointRow): "" | "sukces" | "odmowa" {
  if (row.severity !== "final") return "";
  return row.blocking ? "odmowa" : "sukces";
}

function exitPatch(kind: string): Pick<DatapointRow, "severity" | "blocking"> {
  if (kind === "sukces") return { severity: "final", blocking: false };
  if (kind === "odmowa") return { severity: "final", blocking: true };
  return { severity: null, blocking: false };
}

function nextIndex(items: DatapointRow[]): number {
  return items.reduce((max, item) => Math.max(max, item.index), 0) + 10;
}

/**
 * Kto od kogo zależy w grafie.
 *
 * Świadomie nie rysujemy całego grafu „klocki i strzałki": `forma_wspolpracy`
 * występuje w warunkach 114 węzłów ze 116, więc obrazek byłby pękiem 114 linii
 * z jednego punktu — nieczytelnym i bezużytecznym. Pytanie, na które naprawdę
 * trzeba odpowiedzieć przed zmianą węzła, brzmi „co się zepsuje, jak to ruszę",
 * i na nie odpowiada lista zależności, nie rysunek.
 */
function dependencyMap(items: DatapointRow[]) {
  const dependsOn = new Map<string, string[]>();
  const usedBy = new Map<string, string[]>();

  for (const item of items) {
    const upstream = [...referencedFields(item.conditions)];
    dependsOn.set(item.key, upstream);
    for (const source of upstream) {
      usedBy.set(source, [...(usedBy.get(source) ?? []), item.key]);
    }
  }
  return { dependsOn, usedBy };
}

/** Czy tekst da się pokazać w edytorze wizualnym (i czy to w ogóle poprawny JSON). */
function readConditions(text: string): { node: ConditionNode | null; broken: boolean } {
  const trimmed = text.trim();
  if (!trimmed || trimmed === "null") return { node: null, broken: false };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { node: null, broken: true };
  }
  return supported(parsed)
    ? { node: parsed as ConditionNode, broken: false }
    : { node: null, broken: true };
}

/** Opcje w postaci, którą umie pokazać edytor. `null` = pole bez opcji. */
function readOptions(text: string): { list: OptionRow[] | null; broken: boolean } {
  const trimmed = text.trim();
  if (!trimmed || trimmed === "null") return { list: null, broken: false };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { list: null, broken: true };
  }
  if (!Array.isArray(parsed)) return { list: null, broken: true };
  const ok = parsed.every(
    (item) =>
      item && typeof item === "object" &&
      typeof (item as OptionRow).id === "string" &&
      typeof (item as OptionRow).label === "object",
  );
  return ok ? { list: parsed as OptionRow[], broken: false } : { list: null, broken: true };
}

interface OptionRow {
  id: string;
  label: Record<string, string>;
}

/**
 * Opcje pytania wyboru bez pisania JSON-a.
 *
 * Identyfikator zostaje widoczny, bo to on stoi w warunkach innych pytań —
 * ukrycie go zamieniłoby „zmień etykietę" w „rozepnij pół grafu".
 */
function OptionsEditor({
  value,
  onChange,
}: {
  value: OptionRow[] | null;
  onChange: (next: OptionRow[] | null) => void;
}) {
  const rows = value ?? [];

  const patch = (position: number, next: OptionRow) =>
    onChange(rows.map((row, index) => (index === position ? next : row)));

  const move = (position: number, delta: number) => {
    const target = position + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[position], next[target]] = [next[target], next[position]];
    onChange(next);
  };

  return (
    <div className="adm__tablewrap">
      <table className="adm__i18n">
        <thead>
          <tr>
            <th>Identyfikator</th>
            {LANGS.map((lang) => (
              <th key={lang}>{lang.toUpperCase()}</th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, position) => (
            <tr key={position}>
              <td>
                <input
                  className="input adm__mono"
                  value={row.id}
                  onChange={(event) => patch(position, { ...row, id: event.target.value.trim() })}
                />
              </td>
              {LANGS.map((lang) => (
                <td key={lang}>
                  <input
                    className="input"
                    value={row.label?.[lang] ?? ""}
                    onChange={(event) =>
                      patch(position, { ...row, label: { ...row.label, [lang]: event.target.value } })
                    }
                  />
                </td>
              ))}
              <td className="adm__rowtools">
                <button className="icon-btn" title="Wyżej" onClick={() => move(position, -1)}>
                  ↑
                </button>
                <button className="icon-btn" title="Niżej" onClick={() => move(position, 1)}>
                  ↓
                </button>
                <button
                  className="icon-btn"
                  title="Usuń opcję"
                  onClick={() => {
                    const next = rows.filter((_, index) => index !== position);
                    onChange(next.length ? next : null);
                  }}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={LANGS.length + 2}>
              <button
                className="btn btn--secondary"
                onClick={() => onChange([...rows, { id: "", label: { pl: "" } }])}
              >
                + Dodaj opcję
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function supported(node: unknown): boolean {
  if (typeof node !== "object" || node === null) return false;
  const candidate = node as Record<string, unknown>;
  if (typeof candidate.logic === "string") {
    return (
      (candidate.logic === "AND" || candidate.logic === "OR") &&
      Array.isArray(candidate.rules) &&
      candidate.rules.every(supported)
    );
  }
  return typeof candidate.f === "string" && typeof candidate.op === "string";
}

function Editor({
  schemaId,
  row,
  stages,
  fields,
  exists,
  onClose,
  onSaved,
  onOpen,
}: {
  schemaId: string;
  row: DatapointRow;
  stages: string[];
  fields: DatapointRow[];
  exists: boolean;
  onClose: () => void;
  onSaved: () => void;
  onOpen: (key: string) => void;
}) {
  const [draft, setDraft] = useState<DatapointRow>(row);
  const [conditionsText, setConditionsText] = useState(JSON.stringify(row.conditions ?? null, null, 2));
  const [optionsText, setOptionsText] = useState(JSON.stringify(row.options ?? null, null, 2));
  const [rawConditions, setRawConditions] = useState(false);
  const [rawOptions, setRawOptions] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useScrollLock();

  // Źródłem prawdy zostaje tekst JSON — dzięki temu przełączanie widoku niczego
  // nie gubi, a tryb JSON dalej działa jako wyjście awaryjne dla nietypowych warunków.
  const { node: conditions, broken: conditionsBroken } = useMemo(
    () => readConditions(conditionsText),
    [conditionsText],
  );

  // Warunek może wskazywać dowolne pole schematu, także późniejsze (nawroty w grafie
  // są w procesie legalizacji poprawne), więc lista jest pełna.
  const allFields = useMemo(
    () => [...fields].sort((a, b) => a.index - b.index),
    [fields],
  );

  const dirty =
    JSON.stringify(draft) !== JSON.stringify(row) ||
    conditionsText !== JSON.stringify(row.conditions ?? null, null, 2) ||
    optionsText !== JSON.stringify(row.options ?? null, null, 2);

  const { list: options, broken: optionsBroken } = useMemo(
    () => readOptions(optionsText),
    [optionsText],
  );

  // Cel odczytu musi być pytany po pliku — inaczej kandydat wpisze wartość
  // ręcznie, zanim wgra dokument, a odczyt przyjdzie do zajętego pola.
  const laterFields = useMemo(
    () => allFields.filter((item) => item.index > draft.index && item.key !== draft.key),
    [allFields, draft.index, draft.key],
  );

  const patchUi = (field: string, lang: string, value: string) =>
    setDraft((current) => ({
      ...current,
      ui_params: { ...current.ui_params, [field]: { ...(current.ui_params[field] ?? {}), [lang]: value } },
    }));

  const patchFile = (field: string, value: unknown) =>
    setDraft((current) => {
      const next = { ...(current.file_params ?? {}), [field]: value } as Record<string, unknown>;
      // Pusta wartość znaczy „bez wymagania" — trzymanie jej w bazie zamieniłoby
      // brak decyzji w decyzję („zero plików", „żaden format").
      if (value === "" || value === null || (Array.isArray(value) && value.length === 0)) {
        delete next[field];
      }
      return { ...current, file_params: Object.keys(next).length ? next : null };
    });

  // Predefiniowane pola typów iDenfy — z backendu, żeby lista nie rozjeżdżała się z kodem.
  const [kycTypes, setKycTypes] = useState<KycTypes>({});
  useEffect(() => {
    admin.kycTypes().then(setKycTypes).catch(() => undefined);
  }, []);

  const patchMapping = (field: string, target: string) =>
    setDraft((current) => {
      const next = { ...(current.value_mapping ?? {}) };
      // Puste = „tego odczytu nie zapisujemy"; pusty wpis w bazie byłby decyzją.
      if (target) next[field] = target;
      else delete next[field];
      return { ...current, value_mapping: Object.keys(next).length ? next : null };
    });

  const patchFlag = (field: "id" | "value", value: string) =>
    setDraft((current) => ({
      ...current,
      flag: { id: current.flag?.id ?? "", value: current.flag?.value ?? "", [field]: value },
    }));

  const patchValidation = (field: string, value: string) =>
    setDraft((current) => {
      const next = { ...(current.validation ?? {}) };
      if (value) next[field] = value;
      else delete next[field];
      // Pusty obiekt to nie to samo co brak reguł — zapisujemy `null`,
      // żeby plik schematu wyglądał tak samo jak przed edycją w panelu.
      return { ...current, validation: Object.keys(next).length ? next : null };
    });

  const toggleTarget = (key: string) =>
    setDraft((current) => {
      const picked = new Set(current.extraction_targets ?? []);
      if (picked.has(key)) picked.delete(key);
      else picked.add(key);
      const next = [...picked].sort(
        (a, b) => (allFields.find((f) => f.key === a)?.index ?? 0) -
          (allFields.find((f) => f.key === b)?.index ?? 0),
      );
      return { ...current, extraction_targets: next.length ? next : null };
    });

  const save = async () => {
    setBusy(true);
    setError(null);
    setProblems([]);
    try {
      const payload: DatapointRow = {
        ...draft,
        conditions: parseJson(conditionsText, "warunek"),
        options: parseJson(optionsText, "opcje") as DatapointRow["options"],
      };
      await admin.saveDatapoint(schemaId, payload);
      onSaved();
    } catch (caught) {
      const failure = caught as AdminError;
      setError(failure.message);
      setProblems(failure.problems ?? []);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Usunąć węzeł „${draft.key}"?`)) return;
    setBusy(true);
    try {
      await admin.deleteDatapoint(schemaId, draft.key);
      onSaved();
    } catch (caught) {
      const failure = caught as AdminError;
      setError(failure.message);
      setProblems(failure.problems ?? []);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal__card modal__card--wide">
        <div className="modal__head">
          <h2 className="modal__title">{exists ? `Węzeł: ${draft.key}` : "Nowy węzeł"}</h2>
          <button className="btn btn--ghost" onClick={onClose}>
            Zamknij
          </button>
        </div>

        <div className="modal__body adm__editor">
          <div className="adm__grid">
            <Field label="Klucz">
              <input
                className="input"
                value={draft.key}
                disabled={exists}
                onChange={(event) => setDraft({ ...draft, key: event.target.value.trim() })}
              />
            </Field>
            <Field label="Index (kolejność)">
              <input
                className="input"
                type="number"
                value={draft.index}
                onChange={(event) => setDraft({ ...draft, index: Number(event.target.value) })}
              />
            </Field>
            <Field label="Typ">
              <select
                className="select"
                value={draft.datapoint_type}
                onChange={(event) => setDraft({ ...draft, datapoint_type: event.target.value })}
              >
                {TYPES.map((type) => (
                  <option key={type}>{type}</option>
                ))}
              </select>
            </Field>
            {/* Lista, ale nie zamknięta: nowy proces ma własne etapy, a lista
                w kodzie znała tylko te z legalizacji. Kolejność etapów wynika
                z indeksów pytań, więc nazwę wystarczy wpisać. */}
            <Field label="Etap" hint="Wybierz albo wpisz własny">
              <input
                className="input"
                list="etapy-procesu"
                value={draft.stage ?? ""}
                onChange={(event) => setDraft({ ...draft, stage: event.target.value || null })}
              />
              <datalist id="etapy-procesu">
                {stages.map((stage) => (
                  <option key={stage} value={stage} />
                ))}
              </datalist>
            </Field>
          </div>

          <h3 className="adm__subtitle">Treść pytania</h3>
          <table className="adm__i18n">
            <thead>
              <tr>
                <th />
                {LANGS.map((lang) => (
                  <th key={lang}>{lang.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {UI_FIELDS.map((field) => (
                <tr key={field}>
                  <th>{field}</th>
                  {LANGS.map((lang) => (
                    <td key={lang}>
                      <input
                        className="input"
                        value={draft.ui_params?.[field]?.[lang] ?? ""}
                        onChange={(event) => patchUi(field, lang, event.target.value)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          <div className="adm__subtitle adm__condhead">
            <span>Warunek — kiedy zadajemy to pytanie</span>
            <span className="adm__spacer" />
            <button className="btn btn--ghost" onClick={() => setRawConditions((on) => !on)}>
              {rawConditions ? "Edytor" : "JSON"}
            </button>
          </div>

          {rawConditions ? (
            <Field label="Warunek (JSON)" hint="Puste = pytanie zawsze aktywne">
              <textarea
                className="input adm__json"
                rows={10}
                value={conditionsText}
                onChange={(event) => setConditionsText(event.target.value)}
              />
            </Field>
          ) : conditionsBroken ? (
            // Warunek spoza modelu edytora (ręczna edycja pliku) — nie udajemy,
            // że umiemy go narysować, i nie kasujemy go po cichu.
            <div className="adm__alert">
              Tego warunku nie umiem pokazać w edytorze. Otwórz go w trybie JSON.
            </div>
          ) : (
            <ConditionEditor
              value={conditions}
              fields={allFields}
              selfKey={draft.key}
              onChange={(next) => setConditionsText(JSON.stringify(next, null, 2))}
            />
          )}

          {draft.datapoint_type === "select" && (
            <>
              <div className="adm__subtitle adm__condhead">
                <span>Opcje do wyboru</span>
                <span className="adm__spacer" />
                <button className="btn btn--ghost" onClick={() => setRawOptions((on) => !on)}>
                  {rawOptions ? "Edytor" : "JSON"}
                </button>
              </div>
              {rawOptions ? (
                <Field label="Opcje (JSON)" hint="Wyjście awaryjne — normalnie wystarczy edytor">
                  <textarea
                    className="input adm__json"
                    rows={8}
                    value={optionsText}
                    onChange={(event) => setOptionsText(event.target.value)}
                  />
                </Field>
              ) : optionsBroken ? (
                <div className="adm__alert">
                  Tych opcji nie umiem pokazać w edytorze. Otwórz je w trybie JSON.
                </div>
              ) : (
                <OptionsEditor
                  value={options}
                  onChange={(next) => setOptionsText(JSON.stringify(next, null, 2))}
                />
              )}
            </>
          )}

          {/* Pola, które dotąd dało się ustawić wyłącznie w pliku schematu —
              w panelu nie było ich wcale, więc „edycja procesu bez kodu"
              kończyła się na tekstach pytań. */}
          <h3 className="adm__subtitle">Walidacja odpowiedzi</h3>
          <div className="adm__grid">
            <Field label="Suma kontrolna" hint="Sprawdzana po stronie serwera">
              <select
                className="select"
                value={(draft.validation?.checksum as string) ?? ""}
                onChange={(event) => patchValidation("checksum", event.target.value)}
              >
                <option value="">—</option>
                <option value="pesel">PESEL</option>
                <option value="nip">NIP</option>
                <option value="iban">IBAN (rachunek)</option>
              </select>
            </Field>
            <Field label="Wzorzec (regex)" hint="Np. ^\d{11}$">
              <input
                className="input"
                value={(draft.validation?.regex as string) ?? ""}
                onChange={(event) => patchValidation("regex", event.target.value)}
              />
            </Field>
            <Field label="Maska pola" hint="# to cyfra, np. ###-###-###">
              <input
                className="input"
                value={(draft.validation?.mask as string) ?? ""}
                onChange={(event) => patchValidation("mask", event.target.value)}
              />
            </Field>
          </div>

          {kycTypes[draft.datapoint_type] && (
            <>
              {/* Każdy typ przyjmuje wyłącznie swój dokument z właściwego kraju.
                  Przy każdym odczycie wybieramy datapoint, do którego trafia wartość —
                  bez zmian w kodzie i bez zaszytych kluczy. */}
              <h3 className="adm__subtitle">Mapowanie wartości</h3>
              <p className="adm__muted">
                {kycTypes[draft.datapoint_type].label} — przyjmuje tylko dokument{" "}
                {kycTypes[draft.datapoint_type].doc}{" "}
                {kycTypes[draft.datapoint_type].polish ? "wydany w Polsce" : "wydany poza Polską"}.
                Odczyt bez wybranego datapointu nie jest zapisywany.
              </p>
              <div className="adm__grid" data-testid="value-mapping">
                {kycTypes[draft.datapoint_type].fields.map((pole) => (
                  <Field key={pole.id} label={pole.label} hint={pole.id}>
                    <select
                      className="select"
                      value={draft.value_mapping?.[pole.id] ?? ""}
                      onChange={(event) => patchMapping(pole.id, event.target.value)}
                    >
                      <option value="">— nie zapisuj —</option>
                      {allFields
                        .filter((item) => item.key !== draft.key)
                        .map((item) => (
                          <option key={item.key} value={item.key}>
                            {item.key}
                          </option>
                        ))}
                    </select>
                  </Field>
                ))}
              </div>
            </>
          )}

          {draft.datapoint_type === "set_flag" && (
            <>
              {/* Kandydat nic nie widzi. Gdy ścieżka warunków dojdzie do tego węzła,
                  w sprawie powstaje flaga o tym id i wartości — bez wcześniejszej
                  deklaracji. Ten sam id ustawiony ponownie nadpisuje wartość. */}
              <h3 className="adm__subtitle">Flaga ustawiana w sprawie</h3>
              <div className="adm__grid">
                <Field label="id flagi" hint="Np. ryzyko — flagi nie deklarujemy wcześniej">
                  <input
                    className="input"
                    value={draft.flag?.id ?? ""}
                    placeholder="ryzyko"
                    onChange={(event) => patchFlag("id", event.target.value)}
                  />
                </Field>
                <Field label="value" hint="Np. wysokie — kolejny węzeł z tym id nadpisze wartość">
                  <input
                    className="input"
                    value={draft.flag?.value ?? ""}
                    placeholder="wysokie"
                    onChange={(event) => patchFlag("value", event.target.value)}
                  />
                </Field>
              </div>
            </>
          )}

          {draft.datapoint_type === "file" && (
            <>
              <h3 className="adm__subtitle">Czego oczekujemy po pliku</h3>
              <div className="adm__grid">
                <Field label="Ile plików minimum" hint="Pytanie zostaje, dopóki nie ma kompletu">
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={draft.file_params?.min ?? 1}
                    onChange={(event) => patchFile("min", Number(event.target.value) || 1)}
                  />
                </Field>
                <Field label="Ile maksymalnie">
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={draft.file_params?.max ?? draft.file_params?.min ?? 1}
                    onChange={(event) => patchFile("max", Number(event.target.value) || 1)}
                  />
                </Field>
                <Field label="Formaty" hint="Po przecinku; puste = pdf, jpg, png, heic, webp">
                  <input
                    className="input"
                    placeholder="pdf, jpg, png"
                    value={(draft.file_params?.formats ?? []).join(", ")}
                    onChange={(event) =>
                      patchFile(
                        "formats",
                        event.target.value
                          .split(",")
                          .map((item) => item.trim().toLowerCase().replace(/^\./, ""))
                          .filter(Boolean),
                      )
                    }
                  />
                </Field>
              </div>

              {/* Zdanie czyta model, gdy ocenia wgrany plik. Bez niego „wgraj
                  decyzję" przyjmuje zdjęcie kota, a konsultant odkrywa to trzy
                  dni później. */}
              <Field
                label="Kiedy plik można przyjąć"
                hint="Zdanie dla modelu. Przy wątpliwości plik jest przyjmowany — odmowa musi być pewna"
              >
                <textarea
                  className="input"
                  rows={2}
                  placeholder="Np. decyzja wojewody, czytelna, z widoczną datą ważności"
                  value={draft.file_params?.accept_when ?? ""}
                  onChange={(event) => patchFile("accept_when", event.target.value)}
                />
              </Field>

              <h3 className="adm__subtitle">Co OCR ma odczytać z tego pliku</h3>
              <p className="adm__muted">
                Odczyt trafia do wskazanych pól. Wybrać można tylko te pytane
                później niż sam plik — wcześniejsze kandydat wypełni ręcznie,
                zanim dokument w ogóle wgra, i odczyt przyszedłby za późno.
              </p>
              <div className="dep__list">
                {laterFields.map((item) => {
                  const picked = (draft.extraction_targets ?? []).includes(item.key);
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`dep__chip${picked ? " dep__chip--on" : ""}`}
                      onClick={() => toggleTarget(item.key)}
                      title={item.ui_params?.title?.pl ?? item.key}
                    >
                      {picked ? "✓ " : ""}
                      <span className="adm__mono">{item.key}</span>
                    </button>
                  );
                })}
                {laterFields.length === 0 && (
                  <p className="adm__muted">Za tym plikiem nie ma już żadnych pytań.</p>
                )}
              </div>
            </>
          )}

          <div className="adm__grid">
            {/* Wcześniej stały tu dwa pola — „severity" i „blokujący" — które
                razem znaczyły jedną rzecz: to jest koniec drogi, i czy dobry.
                Proces ma kilka wyjść (zgoda, odmowa z wizą, przejście na B2B),
                więc wybór jest jeden i mówi wprost, którym z nich jest ten węzeł. */}
            <Field label="Punkt wyjścia z procesu" hint="Tylko dla noty — to ostatni ekran ścieżki">
              <select
                className="select"
                value={exitKind(draft)}
                onChange={(event) => setDraft({ ...draft, ...exitPatch(event.target.value) })}
              >
                <option value="">— nie, proces idzie dalej</option>
                <option value="sukces">zakończenie: sprawa załatwiona</option>
                <option value="odmowa">zakończenie: dalej nie możemy</option>
              </select>
            </Field>
            {/* „Wolno zostawić puste" ginęło przy każdym zapisie z panelu,
                bo formularz w ogóle o tym polu nie wiedział. Dotyczy pól, które
                istnieją nie u każdego — jak numer lokalu u mieszkającego w domu. */}
            <Field label="Wolno zostawić puste" hint="Pusta odpowiedź zapisze się jako nie dotyczy">
              <label className="adm__check">
                <input
                  type="checkbox"
                  checked={draft.optional}
                  onChange={(event) => setDraft({ ...draft, optional: event.target.checked })}
                />
                nie każdy to ma
              </label>
            </Field>
          </div>

          {error && (
            <div className="adm__alert adm__alert--error">
              <strong>{error}</strong>
              {problems.length > 0 && (
                <ul>
                  {problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Zależności stały wcześniej między polami formularza i wyglądały jak
            kolejna rzecz do wypełnienia. To nie jest wejście, tylko odpowiedź
            na pytanie „co się zepsuje, jak to ruszę" — więc siedzą poza
            formularzem, za wyraźną kreską i bez ramek pól. */}
        {/* Treść dokumentu to osobny zasób od węzła — ma własny zapis, bo
            zapisanie umowy nie powinno wymagać ruszania pytania i odwrotnie. */}
        {exists && draft.datapoint_type === "simple_sign" && (
          <DocumentEditor schemaId={schemaId} datapointKey={draft.key} />
        )}

        {exists && (
          <Dependencies
            row={draft}
            fields={fields}
            onOpen={(key) => {
              // Skok do sąsiada nie może po cichu wyrzucić niezapisanych zmian.
              if (dirty && !window.confirm("Masz niezapisane zmiany. Otworzyć inny węzeł?")) return;
              onOpen(key);
            }}
          />
        )}

        <div className="modal__head adm__footer">
          {exists && (
            /* Skasowanie węzła mówi maliną w tekście (adm__risky) — pełne
               wypełnienie jest zarezerwowane dla akcji głównej. */
            <button className="btn btn--ghost adm__risky" disabled={busy} onClick={remove}>
              Usuń węzeł
            </button>
          )}
          <span className="adm__spacer" />
          <button className="btn btn--secondary" onClick={onClose}>
            Anuluj
          </button>
          <button className="btn btn--primary" disabled={busy || !draft.key} onClick={save}>
            {busy ? "Zapisuję…" : "Zapisz"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Treść dokumentu, który kandydat podpisuje na tym węźle.
 *
 * Dokumenty były wpisane w kod: poprawka literówki w umowie znaczyła zmianę
 * w repozytorium i deploy. Tekst jest zwykłym tekstem z `{{placeholderami}}`,
 * a nazwy pól sprawdza serwer przy zapisie — literówka nie ma prawa dojechać
 * do wydruku, bo w PDF-ie zostawia puste miejsce w podpisanej już umowie.
 *
 * Brak własnej treści znaczy „drukuj szablon wbudowany", więc procesy sprzed
 * tej zmiany wyglądają dokładnie tak jak dotąd.
 */
function DocumentEditor({ schemaId, datapointKey }: { schemaId: string; datapointKey: string }) {
  const [lang, setLang] = useState<(typeof LANGS)[number]>("pl");
  const [title, setTitle] = useState<Record<string, string>>({});
  const [body, setBody] = useState<Record<string, string>>({});
  const [placeholders, setPlaceholders] = useState<{ name: string; description: string }[]>([]);
  const [exists, setExists] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [counterparty, setCounterparty] = useState(false);
  const [cpBusy, setCpBusy] = useState(false);
  // Dokument wbudowany tego pola (jeśli go ma) + zapisana podmiana jego tekstów.
  const [bundled, setBundled] = useState<BundledDoc | null>(null);
  const [overrides, setOverrides] = useState<TextOverride>({});
  // Dwie drogi dostosowania: „override" poprawia teksty wbudowanego (zachowuje
  // logikę TAK/NIE), „custom" zastępuje dokument własną treścią z placeholderami.
  const [mode, setMode] = useState<"override" | "custom">("custom");

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const data = await admin.templates(schemaId);
        if (!alive) return;
        const mine = data.items.find((item) => item.datapoint_key === datapointKey);
        const bun = data.bundled?.[datapointKey] ?? null;
        setPlaceholders(data.placeholders);
        setCounterparty(data.counterparty_signature);
        setExists(Boolean(mine));
        setTitle(mine?.title ?? {});
        setBody(mine?.body ?? {});
        setBundled(bun);
        setOverrides(mine?.overrides ?? {});
        // Zapisany wybór wygrywa; bez wpisu domyślnie poprawiamy wbudowany
        // (jeśli istnieje), a dokument bez wbudowanego można tylko napisać od zera.
        setMode(
          mine?.kind === "custom" ? "custom" : mine?.kind === "override" ? "override" : bun ? "override" : "custom",
        );
      } catch (caught) {
        if (alive) setError((caught as AdminError).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [schemaId, datapointKey]);

  // Wstawiamy w miejsce kursora, a nie na koniec: umowę pisze się w środku zdania.
  const insert = (name: string) => {
    const area = document.getElementById("tpl-body") as HTMLTextAreaElement | null;
    const text = body[lang] ?? "";
    const at = area ? area.selectionStart : text.length;
    const next = `${text.slice(0, at)}{{${name}}}${text.slice(at)}`;
    setBody({ ...body, [lang]: next });
    setSaved(false);
    window.requestAnimationFrame(() => {
      if (!area) return;
      area.focus();
      area.selectionStart = area.selectionEnd = at + name.length + 4;
    });
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setProblems([]);
    try {
      const result = await admin.saveTemplate(schemaId, datapointKey, { title, body });
      // Serwer przepisuje nazwy ze wzoru zleceniodawcy na nasze, więc pokazujemy
      // to, co naprawdę zapisał — inaczej edytor kłamałby o treści w bazie.
      setTitle(result.title);
      setBody(result.body);
      setWarnings(result.warnings);
      setExists(true);
      setSaved(true);
    } catch (caught) {
      const failure = caught as AdminError;
      setError(failure.message);
      setProblems(failure.problems ?? []);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Skasować własną treść i wrócić do szablonu wbudowanego?")) return;
    setBusy(true);
    try {
      await admin.deleteTemplate(schemaId, datapointKey);
      setExists(false);
      setTitle({});
      setBody({});
      setWarnings([]);
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  // Podpis drugiej strony to obraz jeden na proces — plik z dysku idzie do
  // serwera jako data-URI, a w treści umowy wstawia się go placeholderem.
  const wgrajPodpis = async (plik: File) => {
    setCpBusy(true);
    setError(null);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("Nie udało się odczytać pliku."));
        reader.readAsDataURL(plik);
      });
      const wynik = await admin.setCounterpartySignature(schemaId, dataUrl);
      setCounterparty(wynik.set);
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setCpBusy(false);
    }
  };

  const usunPodpis = async () => {
    if (!window.confirm("Usunąć podpis drugiej strony z tego procesu?")) return;
    setCpBusy(true);
    try {
      await admin.setCounterpartySignature(schemaId, null);
      setCounterparty(false);
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setCpBusy(false);
    }
  };

  return (
    <section className="deps">
      <p className="deps__lead">
        <strong>Dokument do podpisu</strong> —{" "}
        {mode === "override"
          ? "poprawiasz teksty i tłumaczenia dokumentu wbudowanego. Logika (co się drukuje przy jakiej odpowiedzi) zostaje w kodzie."
          : exists
            ? "własna treść tego procesu."
            : bundled
              ? "ten węzeł drukuje szablon wbudowany. Możesz poprawić jego teksty albo zastąpić go własnym dokumentem."
              : "ten węzeł drukuje szablon wbudowany. Wpisanie treści go zastąpi."}{" "}
        Polska wersja jest wiążąca, pozostałe języki to tłumaczenia pomocnicze.
      </p>

      {bundled && (
        <div className="adm__toolbar" style={{ marginBottom: 6 }}>
          <button
            className={`btn ${mode === "override" ? "btn--primary" : "btn--ghost"}`}
            onClick={() => setMode("override")}
          >
            Popraw wbudowany
          </button>
          <button
            className={`btn ${mode === "custom" ? "btn--primary" : "btn--ghost"}`}
            onClick={() => setMode("custom")}
          >
            Zastąp własnym
          </button>
        </div>
      )}

      {mode === "override" && bundled ? (
        <OverrideEditor
          schemaId={schemaId}
          datapointKey={datapointKey}
          bundled={bundled}
          initial={overrides}
          onSaved={setOverrides}
        />
      ) : (
       <>
      <div className="adm__toolbar">
        <select
          className="select"
          value={lang}
          onChange={(event) => setLang(event.target.value as (typeof LANGS)[number])}
          aria-label="Język dokumentu"
        >
          {LANGS.map((code) => (
            <option key={code} value={code}>
              {code.toUpperCase()}
              {(body[code] ?? "").trim() ? " ●" : ""}
            </option>
          ))}
        </select>
        <span className="adm__spacer" />
        {exists && (
          /* Kasuje własną treść dokumentu — akcja niszcząca, więc malina
             w tekście, nie pełne wypełnienie. */
          <button className="btn btn--ghost adm__risky" disabled={busy} onClick={remove}>
            Wróć do wbudowanego
          </button>
        )}
        <button className="btn btn--primary" disabled={busy} onClick={save}>
          {busy ? "Zapisuję…" : "Zapisz dokument"}
        </button>
      </div>

      <Field label={`Tytuł (${lang.toUpperCase()})`}>
        <input
          className="input"
          value={title[lang] ?? ""}
          onChange={(event) => {
            setTitle({ ...title, [lang]: event.target.value });
            setSaved(false);
          }}
        />
      </Field>

      <Field label={`Treść (${lang.toUpperCase()})`} hint="Pusty wiersz rozdziela akapity">
        <textarea
          id="tpl-body"
          className="input adm__json"
          rows={12}
          value={body[lang] ?? ""}
          onChange={(event) => {
            setBody({ ...body, [lang]: event.target.value });
            setSaved(false);
          }}
        />
      </Field>

      {/* Wstawek jest kilkadziesiąt — cała lista rozpychała okno na kilka
          ekranów i redaktor tracił z oczu treść, którą pisze. Lista przewija
          się u siebie, a nie ciągnie za sobą całego okna. */}
      <span className="adm__fieldlabel">Do wstawienia — kliknięcie wstawia w miejscu kursora</span>
      <div className="dep__list dep__list--scroll">
        {placeholders.map((item) => (
          <button
            key={item.name}
            type="button"
            className="dep__chip"
            title={item.description}
            onClick={() => insert(item.name)}
          >
            <span className="adm__mono">{`{{${item.name}}}`}</span>
          </button>
        ))}
      </div>

      {/* Podpis drugiej strony: obraz jeden na proces, wstawiany placeholderem,
          pojawia się dopiero na egzemplarzu podpisanym przez kandydata. */}
      <p className="deps__lead" style={{ marginTop: 18 }}>
        <strong>Podpis drugiej strony</strong> — {counterparty ? "wgrany." : "nie wgrany."} Wstaw go w
        treść przez <span className="adm__mono">{"{{podpis_zleceniodawcy}}"}</span> (można w kilku
        miejscach); pojawi się dopiero na egzemplarzu podpisanym przez kandydata.
      </p>
      <div className="adm__toolbar" style={{ marginTop: 4 }}>
        <label className="btn btn--secondary">
          {cpBusy ? "Wgrywam…" : counterparty ? "Zmień podpis" : "Wgraj podpis"}
          <input
            type="file"
            accept="image/png,image/jpeg"
            hidden
            disabled={cpBusy}
            onChange={(event) => {
              const plik = event.target.files?.[0];
              if (plik) void wgrajPodpis(plik);
              event.target.value = "";
            }}
          />
        </label>
        {counterparty && (
          <button className="btn btn--ghost adm__risky" disabled={cpBusy} onClick={usunPodpis}>
            Usuń podpis
          </button>
        )}
      </div>

      {/* Potwierdzenie zapisu dociska się sprężyną (klasa z tokens.css) —
          widać, że coś się właśnie stało, a nie wisiało tu od zawsze. */}
      {saved && !error && <p className="adm__muted potwierdzenie">Zapisano.</p>}
      {warnings.length > 0 && (
        <div className="adm__alert">
          <ul>
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
      {error && (
        <div className="adm__alert adm__alert--error">
          <strong>{error}</strong>
          {problems.length > 0 && (
            <ul>
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
        </div>
      )}
       </>
      )}
    </section>
  );
}

/**
 * Podmiana tekstów dokumentu wbudowanego (oświadczenie ZUS/US i pokrewne).
 *
 * Treść tych dokumentów jest w kodzie, bo ich logika — który punkt drukuje się
 * TAK, który NIE, który „nie dotyczy" — jest treścią reguły, nie tekstem do
 * przeklikania. Ale brzmienie i tłumaczenia zleceniodawca chce poprawiać sam.
 * Tu edytuje się każdy tekst z osobna; puste pole zostawia wersję wbudowaną,
 * więc podmiana jednego zdania nie każe przepisywać całego dokumentu.
 */
function OverrideEditor({
  schemaId,
  datapointKey,
  bundled,
  initial,
  onSaved,
}: {
  schemaId: string;
  datapointKey: string;
  bundled: BundledDoc;
  initial: TextOverride;
  onSaved?: (ov: TextOverride) => void;
}) {
  const [lang, setLang] = useState<(typeof LANGS)[number]>("pl");
  const [ov, setOv] = useState<TextOverride>(initial);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);

  const domyslny = (field: "title" | "intro" | "closing") =>
    bundled[field]?.[lang] ?? bundled[field]?.pl ?? "";
  const wartosc = (field: "title" | "intro" | "closing") => ov[field]?.[lang] ?? "";
  const wartoscPunktu = (key: string) => ov.claims?.[key]?.[lang] ?? "";
  const domyslnyPunkt = (key: string) => {
    const c = bundled.claims.find((x) => x.key === key);
    return c?.text?.[lang] ?? c?.text?.pl ?? "";
  };

  const ustawPole = (field: "title" | "intro" | "closing", value: string) => {
    setOv((prev) => ({ ...prev, [field]: { ...(prev[field] ?? {}), [lang]: value } }));
    setSaved(false);
  };
  const ustawPunkt = (key: string, value: string) => {
    setOv((prev) => ({
      ...prev,
      claims: { ...(prev.claims ?? {}), [key]: { ...((prev.claims ?? {})[key] ?? {}), [lang]: value } },
    }));
    setSaved(false);
  };

  // Kropka przy języku, gdy cokolwiek w nim podmieniono — konsultant widzi,
  // które tłumaczenia już ruszył, bez otwierania każdego z osobna.
  const langMaPodmiane = (code: string) =>
    [ov.title, ov.intro, ov.closing, ...Object.values(ov.claims ?? {})].some(
      (slownik) => (slownik?.[code] ?? "").trim(),
    );

  const save = async () => {
    setBusy(true);
    setError(null);
    setProblems([]);
    try {
      await admin.setTemplateOverride(schemaId, datapointKey, ov);
      onSaved?.(ov);
      setSaved(true);
    } catch (caught) {
      const failure = caught as AdminError;
      setError(failure.message);
      setProblems(failure.problems ?? []);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Wyczyścić wszystkie podmiany i wrócić do dokumentu wbudowanego?")) return;
    setBusy(true);
    setError(null);
    try {
      await admin.setTemplateOverride(schemaId, datapointKey, {});
      setOv({});
      onSaved?.({});
      setSaved(true);
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="adm__toolbar">
        <select
          className="select"
          value={lang}
          onChange={(event) => setLang(event.target.value as (typeof LANGS)[number])}
          aria-label="Język dokumentu"
        >
          {LANGS.map((code) => (
            <option key={code} value={code}>
              {code.toUpperCase()}
              {langMaPodmiane(code) ? " ●" : ""}
            </option>
          ))}
        </select>
        <span className="adm__spacer" />
        <button className="btn btn--ghost adm__risky" disabled={busy} onClick={reset}>
          Wyczyść podmiany
        </button>
        <button className="btn btn--primary" disabled={busy} onClick={save}>
          {busy ? "Zapisuję…" : "Zapisz podmiany"}
        </button>
      </div>

      <Field label={`Tytuł (${lang.toUpperCase()})`} hint="Puste = tekst wbudowany">
        <input
          className="input"
          value={wartosc("title")}
          placeholder={domyslny("title")}
          onChange={(event) => ustawPole("title", event.target.value)}
        />
      </Field>

      <Field label={`Wstęp (${lang.toUpperCase()})`} hint="Puste = tekst wbudowany">
        <textarea
          className="input adm__json"
          rows={2}
          value={wartosc("intro")}
          placeholder={domyslny("intro")}
          onChange={(event) => ustawPole("intro", event.target.value)}
        />
      </Field>

      {bundled.claims.length > 0 && (
        <span className="adm__fieldlabel">
          Punkty oświadczenia — puste pole zostawia tekst wbudowany, logika TAK/NIE zostaje
        </span>
      )}
      {bundled.claims.map((claim, index) => (
        <Field key={claim.key} label={`Punkt ${index + 1} (${lang.toUpperCase()})`} hint={claim.text.pl}>
          <textarea
            className="input adm__json"
            rows={2}
            value={wartoscPunktu(claim.key)}
            placeholder={domyslnyPunkt(claim.key)}
            onChange={(event) => ustawPunkt(claim.key, event.target.value)}
          />
        </Field>
      ))}

      {(bundled.closing?.pl || wartosc("closing")) && (
        <Field label={`Zamknięcie (${lang.toUpperCase()})`} hint="Puste = tekst wbudowany">
          <textarea
            className="input adm__json"
            rows={3}
            value={wartosc("closing")}
            placeholder={domyslny("closing")}
            onChange={(event) => ustawPole("closing", event.target.value)}
          />
        </Field>
      )}

      {saved && !error && <p className="adm__muted potwierdzenie">Zapisano.</p>}
      {error && (
        <div className="adm__alert adm__alert--error">
          <strong>{error}</strong>
          {problems.length > 0 && (
            <ul>
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Co się zepsuje, jeśli ruszę ten węzeł.
 *
 * Backend odmawia skasowania węzła, od którego zależą warunki innych pytań —
 * ale dowiadywało się o tym dopiero przy zapisie. Tutaj widać to od razu,
 * razem z drugą konsekwencją, o której łatwo zapomnieć: zmiana odpowiedzi
 * u korzenia gałęzi uruchamia kaskadową czystkę wszystkiego, co z niej wynikało.
 */
function Dependencies({
  row,
  fields,
  onOpen,
}: {
  row: DatapointRow;
  fields: DatapointRow[];
  onOpen: (key: string) => void;
}) {
  const { dependsOn, usedBy } = useMemo(() => dependencyMap(fields), [fields]);
  const byKey = useMemo(() => new Map(fields.map((item) => [item.key, item])), [fields]);

  const upstream = dependsOn.get(row.key) ?? [];
  const downstream = usedBy.get(row.key) ?? [];

  const chip = (key: string) => {
    const target = byKey.get(key);
    return (
      <button key={key} type="button" className="dep__chip" onClick={() => onOpen(key)}>
        <span className="adm__mono">{key}</span>
        {target?.ui_params?.title?.pl && <span className="adm__muted"> — {target.ui_params.title.pl}</span>}
      </button>
    );
  };

  return (
    <section className="deps">
      <p className="deps__lead">
        <strong>Zależności</strong> — nie do wypełnienia. Tak ten węzeł jest
        powiązany z resztą procesu; kliknięcie otwiera sąsiada.
      </p>
      <div className="adm__grid adm__grid--2">
        <div className="dep">
          <span className="adm__fieldlabel">Pyta o to dopiero, gdy znamy ({upstream.length})</span>
          {upstream.length === 0 ? (
            <p className="adm__muted">Bez warunku — pytanie jest zawsze aktywne.</p>
          ) : (
            <div className="dep__list">{upstream.map(chip)}</div>
          )}
        </div>

        <div className="dep">
          <span className="adm__fieldlabel">Od tego zależą ({downstream.length})</span>
          {downstream.length === 0 ? (
            <p className="adm__muted">Nic nie zależy od tej odpowiedzi.</p>
          ) : (
            <>
              <div className="dep__list">{downstream.slice(0, 12).map(chip)}</div>
              {downstream.length > 12 && (
                <p className="adm__muted">…i jeszcze {downstream.length - 12}</p>
              )}
              <p className="dep__warn">
                Zmiana tej odpowiedzi u kandydata wyczyści {downstream.length}{" "}
                {downstream.length === 1 ? "zależne pytanie" : "zależnych pytań"} (kaskadowa czystka),
                a skasowanie węzła zostanie odrzucone przez walidację.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="adm__field">
      <span className="adm__fieldlabel">{label}</span>
      {children}
      {hint && <span className="adm__muted">{hint}</span>}
    </label>
  );
}

function parseJson(text: string, what: string): unknown {
  const trimmed = text.trim();
  if (!trimmed || trimmed === "null") return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    throw new AdminError(400, `Nie mogę odczytać pola „${what}" — to nie jest poprawny JSON.`);
  }
}
